import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Trash2, CheckCircle, Clock, X, Eye, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => { loadMessages(); }, []);

  const loadMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/messages`, { headers: getHeaders() });
      setMessages(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/api/admin/messages/${id}/status`, { status }, { headers: getHeaders() });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));
      if (selectedMessage?.id === id) setSelectedMessage(prev => ({ ...prev, status }));
    } catch (err) { console.error(err); }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/api/admin/messages/${id}`, { headers: getHeaders() });
      setMessages(prev => prev.filter(m => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch (err) { console.error(err); }
  };

  const filtered = messages.filter(m => {
    const matchSearch = !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase()) || m.organization?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || m.status === filter;
    return matchSearch && matchFilter;
  });

  const statusBadge = (status) => {
    const styles = { new: 'bg-blue-50 text-blue-600', contacted: 'bg-emerald-50 text-emerald-600', closed: 'bg-slate-100 text-slate-500' };
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase ${styles[status] || styles.new}`}>{status}</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="messages-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Messages</h1>
          <p className="text-sm text-slate-400 mt-1">{messages.length} total submissions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
          <input
            data-testid="messages-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages..."
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
          />
        </div>
        <div className="flex gap-1.5 bg-white rounded-xl border border-slate-200 p-1">
          {['all', 'new', 'contacted', 'closed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? 'bg-blue-50 text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-6">
        {/* Messages list */}
        <div className={`flex-1 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden ${selectedMessage ? 'hidden lg:block' : ''}`}>
          <div className="divide-y divide-slate-50">
            {filtered.length === 0 ? (
              <div className="px-6 py-16 text-center">
                <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-300">No messages found</p>
              </div>
            ) : filtered.map(msg => (
              <div
                key={msg.id}
                data-testid={`message-row-${msg.id}`}
                className={`px-6 py-4 flex items-start gap-4 cursor-pointer transition-colors ${selectedMessage?.id === msg.id ? 'bg-blue-50/50' : 'hover:bg-slate-50/50'}`}
                onClick={() => setSelectedMessage(msg)}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                  msg.status === 'new' ? 'bg-blue-100 text-blue-600' : msg.status === 'contacted' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
                }`}>
                  {msg.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-slate-700">{msg.name}</span>
                    {statusBadge(msg.status)}
                  </div>
                  <p className="text-xs text-slate-400">{msg.organization} - {msg.email}</p>
                  <p className="text-sm text-slate-500 mt-1 truncate">{msg.message}</p>
                </div>
                <span className="text-xs text-slate-300 flex-shrink-0">
                  {msg.submitted_at ? format(new Date(msg.submitted_at), 'MMM d, HH:mm') : ''}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Message detail */}
        {selectedMessage && (
          <div className="w-full lg:w-[420px] bg-white rounded-2xl border border-slate-100 shadow-sm flex-shrink-0">
            <div className="p-6 border-b border-slate-50 flex items-center justify-between">
              <h3 className="font-semibold text-slate-700">Message Details</h3>
              <button onClick={() => setSelectedMessage(null)} className="p-1 text-slate-300 hover:text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Name</label>
                <p className="text-sm text-slate-700 font-medium mt-1">{selectedMessage.name}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Email</label>
                <p className="text-sm text-blue-600 mt-1"><a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a></p>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Phone</label>
                <p className="text-sm mt-1" dir="ltr">{selectedMessage.phone ? <a href={`tel:${selectedMessage.phone}`} className="text-blue-600">{selectedMessage.phone}</a> : <span className="text-slate-300">-</span>}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Organization</label>
                <p className="text-sm text-slate-700 mt-1">{selectedMessage.organization || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Message</label>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed whitespace-pre-wrap bg-slate-50 p-4 rounded-xl">{selectedMessage.message}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Status</label>
                <div className="mt-2">{statusBadge(selectedMessage.status)}</div>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Received</label>
                <p className="text-sm text-slate-500 mt-1">{selectedMessage.submitted_at ? format(new Date(selectedMessage.submitted_at), 'PPPp') : ''}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-slate-50">
                {selectedMessage.status !== 'contacted' && (
                  <button
                    data-testid="mark-contacted-btn"
                    onClick={() => updateStatus(selectedMessage.id, 'contacted')}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-50 text-emerald-600 text-sm font-medium py-2.5 rounded-xl hover:bg-emerald-100 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" /> Contacted
                  </button>
                )}
                {selectedMessage.status !== 'closed' && (
                  <button
                    onClick={() => updateStatus(selectedMessage.id, 'closed')}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-slate-50 text-slate-500 text-sm font-medium py-2.5 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <Clock className="w-4 h-4" /> Close
                  </button>
                )}
                <button
                  data-testid="delete-message-btn"
                  onClick={() => deleteMessage(selectedMessage.id)}
                  className="flex items-center justify-center gap-1.5 bg-red-50 text-red-500 text-sm font-medium py-2.5 px-4 rounded-xl hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
